import {
  getSentry,
  getFileExtension,
  getBrowserInfo,
  getNetworkType,
  calculateTransferSpeed,
  sentryContextManager,
  type ActivityContext,
  type UploadActivityData,
  type TrackedUploadLocation,
} from './sentryUtils';
import type { FileSetProgress } from '@mimir/common/upload/uploadProgression';
import type { ProgressInfo } from '@mimir/common/dto/progress';

// Helper to map uploadLocationId to readable upload client
function getUploadClient(uploadLocationId?: TrackedUploadLocation): string {
  switch (uploadLocationId) {
    case 'shared_upload':
    case 'shared_folder_upload':
      return 'share_page';
    case 'web_client_upload':
      return 'web_client';
    default:
      return 'unknown';
  }
}

// Helper to get upload activity from context manager
function getUploadActivity(uploadId: string): ActivityContext | undefined {
  return sentryContextManager.getActivities('upload').find(a => a.id === uploadId);
}

// Helper to get upload data from activity
function getUploadData(activity: ActivityContext): UploadActivityData {
  return activity.data;
}

// Register upload activity type with the generic context manager
function registerUploadActivityType() {
  sentryContextManager.registerActivityType({
    name: 'upload',
    contextKey: 'active_uploads',
    tags: {
      countTag: 'active_upload_count',
      idsTag: undefined, // We'll use custom tag for Mimir IDs
      customTags: {
        'active_mimir_ids': (activities) => {
          const mimirIds: string[] = [];
          activities.forEach(activity => {
            const data = getUploadData(activity);
            if (data.mimirItemId) {
              mimirIds.push(data.mimirItemId);
            } else {
              mimirIds.push(`pending_${activity.id}`);
            }
          });
          return mimirIds.length > 0 ? mimirIds.join(',') : null;
        },
        'upload_client': (activities) => {
          // Get upload client from first activity
          const firstActivity = activities[0];
          if (!firstActivity) return null;
          const data = getUploadData(firstActivity);
          return getUploadClient(data.uploadLocationId);
        }
      }
    },
    contextTransformer: (activities) => {
      const uploadsContext: Record<string, any> = {};

      activities.forEach(activity => {
        const data = getUploadData(activity);
        const key = data.mimirItemId || activity.id;
        const uploadClient = getUploadClient(data.uploadLocationId);

        uploadsContext[key] = {
          localId: activity.id,
          mimirItemId: data.mimirItemId || 'pending',
          fileExtension: data.fileExtension,
          fileSize: data.fileSize,
          fileType: data.fileType,
          uploadStartTime: activity.startTime,
          uploadDurationSeconds: Math.round((Date.now() - activity.startTime) / 1000),
          isMultipart: data.isMultipart,
          uploadLocationId: data.uploadLocationId,
          uploadClient: uploadClient,
          // Flatten browserInfo so it shows properly in Sentry instead of [Object]
          browserName: data.browserInfo.browserName,
          browserVersion: data.browserInfo.browserVersion,
          memoryMB: data.browserInfo.memory,
          isOnline: data.browserInfo.onLine
        };
      });

      return uploadsContext;
    }
  });
}


export function initializeUploadTracking() {
  if (!getSentry()) {
    return;
  }

  // Register upload activity type
  registerUploadActivityType();
  // Monitor page unload events
  window.addEventListener('beforeunload', () => {
    const activeUploads = sentryContextManager.getActivities('upload');

    const sentry = getSentry();
    if (activeUploads.length > 0 && sentry) {

      sentry.captureMessage('User navigating away with active uploads', {
        level: 'warning',
        tags: {
          navigation_with_uploads: 'true'
        },
        extra: {
          navigationTimestamp: Date.now(),
          note: 'See active_uploads context for detailed upload information'
        }
      });

      // Improved flushing strategy for better delivery during page unload
      const client = sentry.getClient();
      if (client) {
        void client.flush(1000);

        // Also try transport flush as backup
        const transport = client.getTransport();
        if (transport) {
          void transport.flush(1000);
        }
      }
    }
  });

  // Monitor network changes specific to uploads
  window.addEventListener('offline', () => {
    const activeUploads = sentryContextManager.getActivities('upload');
    const sentry = getSentry();
    if (activeUploads.length > 0 && sentry) {
      sentry.captureMessage('Network lost during active uploads', {
        level: 'error',
        tags: {
          network_lost_during_uploads: 'true'
          // Note: active_upload_count and active_mimir_ids are already set globally
        },
        extra: {
          networkLostTimestamp: Date.now(),
          note: 'See active_uploads context for affected uploads with full details'
        }
      });
    }
  });

  // Monitor visibility changes specific to uploads
  document.addEventListener('visibilitychange', () => {
    const activeUploads = sentryContextManager.getActivities('upload');
    if (document.hidden && activeUploads.length > 0) {
      const sentry = getSentry();
      if (sentry) {
        sentry.addBreadcrumb({
          category: 'visibility',
          message: 'Tab became hidden during upload',
          level: 'info',
          data: {
            note: 'Active uploads available in Sentry context'
          }
        });
      }
    }
  });
}

// Interface for tracking uploads - can be UploadItem or simplified tracking object
export interface UploadTrackingItem {
  id: string;
  file: { size: number; type: string; name: string };
  uploadLocationId?: TrackedUploadLocation;
  chunkSize?: number;
  useMultiPart?: boolean;
  isExistingItem?: boolean;
}

export function trackUploadStart(uploadItem: UploadTrackingItem) {
  // Ensure upload activity type is registered (defensive programming)
  registerUploadActivityType();
  // Create upload activity with all the necessary data
  const data: UploadActivityData = {
    mimirItemId: uploadItem.isExistingItem ? uploadItem.id : undefined,
    fileExtension: getFileExtension(uploadItem.file.name),
    fileSize: uploadItem.file.size,
    fileType: uploadItem.file.type || 'unknown',
    uploadLocationId: uploadItem.uploadLocationId,
    isMultipart: uploadItem.useMultiPart || false,
    chunkSize: uploadItem.chunkSize,
    totalChunks: uploadItem.useMultiPart && uploadItem.chunkSize
      ? Math.ceil(uploadItem.file.size / uploadItem.chunkSize)
      : undefined,
    networkSpeed: undefined,
    browserInfo: getBrowserInfo()
  };
  const uploadActivity: ActivityContext = {
    id: uploadItem.id,
    type: 'upload',
    status: 'uploading',
    startTime: Date.now(),
    data,
  };

  // Add upload activity to the context manager
  sentryContextManager.setActivity('upload', uploadActivity);

  // Set general upload context for this session
  const sentryInstance = getSentry();
  if (sentryInstance) {
    const activeUploads = sentryContextManager.getActivities('upload');
    sentryInstance.setContext('upload_session', {
      activeUploads: activeUploads.length,
      timestamp: Date.now()
    });
  }
}

// Call this when the real Mimir GUID becomes available (after item creation)
export function updateUploadItemId(localId: string, realMimirId: string) {
  const activity = getUploadActivity(localId);
  if (activity) {
    const data = getUploadData(activity);
    data.mimirItemId = realMimirId;
    // Update the activity in the context manager
    sentryContextManager.setActivity('upload', activity);
  }
}

export function trackUploadProgress(
  itemId: string,
  _progress: FileSetProgress | ProgressInfo,
  bytesUploaded?: number
) {
  const activity = getUploadActivity(itemId);
  if (!activity) return;

  const now = Date.now();
  const duration = now - activity.startTime;
  const speed = bytesUploaded ? calculateTransferSpeed(bytesUploaded, duration) : undefined;

  if (speed) {
    const data = getUploadData(activity);
    data.networkSpeed = speed;
    // Update activity to reflect new network speed
    sentryContextManager.setActivity('upload', activity);
  }
}

export function trackUploadError(
  itemId: string,
  error: Error | unknown,
  bytesUploaded: number = 0,
  retryCount: number = 0
) {
  const activity = getUploadActivity(itemId);
  const sentry = getSentry();
  if (!sentry) return;

  // Collect upload queue state
  const activeUploads = sentryContextManager.getActivities('upload');
  const uploadQueueState = {
    totalActiveUploads: activeUploads.length,
    activeUploadIds: activeUploads.map(a => a.id),
    queueSnapshot: activeUploads.map(a => {
      const uploadData = getUploadData(a);
      return {
        localId: a.id,
        mimirItemId: uploadData.mimirItemId || 'pending',
        fileType: uploadData.fileType,
        fileSize: uploadData.fileSize
      };
    })
  };

  if (!activity) {
    // If no activity, still track the error with available info
    sentry.captureException(error, {
      tags: {
        upload_local_id: itemId,
        upload_error_type: error instanceof Error ? error.name : 'Unknown',
        upload_error_no_context: 'true',
        upload_source: 'unknown',
        total_active_uploads: String(uploadQueueState.totalActiveUploads)
      },
      extra: {
        errorMessage: error instanceof Error ? error.message : String(error),
        localId: itemId,
        uploadQueueState
      }
    });
    return;
  }

  const data = getUploadData(activity);
  const now = Date.now();
  const duration = now - activity.startTime;
  const percentComplete = data.fileSize > 0
    ? Math.round((bytesUploaded / data.fileSize) * 100)
    : 0;

  // Extract HTTP status if available
  let httpStatus: number | undefined;
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { status?: number } }).response;
    if (response && typeof response.status === 'number') {
      httpStatus = response.status;
    }
  }

  // Capture the error with full context
  sentry.captureException(error, {
    tags: {
      upload_error_type: error instanceof Error ? error.name : 'Unknown',
      upload_file_type: data.fileType,
      upload_multipart: String(data.isMultipart),
      upload_percent_complete: String(percentComplete),
      upload_local_id: activity.id,
      mimir_item_id: data.mimirItemId || 'pending',
      upload_network_type: getNetworkType() || 'unknown',
      upload_client: getUploadClient(data.uploadLocationId),
      total_active_uploads: String(uploadQueueState.totalActiveUploads)
    },
    extra: {
      // Both IDs for complete traceability
      localId: activity.id,
      mimirItemId: data.mimirItemId,
      fileExtension: data.fileExtension,
      fileSize: data.fileSize,
      bytesUploaded,
      uploadDuration: Math.round(duration / 1000), // seconds
      uploadSpeed: data.networkSpeed,
      httpStatus,
      networkType: getNetworkType(),
      retryCount,
      browserInfo: data.browserInfo,
      isOnline: navigator.onLine,
      uploadQueueState
    }
  });

  // Add error breadcrumb
  sentry.addBreadcrumb({
    category: 'upload.error',
    message: `Upload failed: ${data.fileExtension}`,
    level: 'error',
    data: {
      mimirItemId: itemId,
      fileExtension: data.fileExtension,
      errorMessage: error instanceof Error ? error.message : String(error),
      percentComplete,
      httpStatus,
      duration: Math.round(duration / 1000)
    }
  });
}

export function trackUploadComplete(itemId: string, _bytesUploaded: number) {
  const activity = getUploadActivity(itemId);
  if (!activity) return;

  // Simply clean up the tracking context
  sentryContextManager.removeActivity('upload', itemId);
}

export function trackUploadCancelled(itemId: string, reason?: string) {
  const activity = getUploadActivity(itemId);
  if (!activity) return;

  const data = getUploadData(activity);
  const duration = Date.now() - activity.startTime;

  const sentry = getSentry();
  if (sentry) {
    sentry.addBreadcrumb({
      category: 'upload.cancelled',
      message: `Upload cancelled: ${data.fileExtension}`,
      level: 'warning',
      data: {
        itemId,
        fileExtension: data.fileExtension,
        reason,
        duration: Math.round(duration / 1000) // seconds
      }
    });

    // Capture cancellation event
    sentry.captureMessage('Upload cancelled by user', {
      level: 'info',
      extra: {
        fileExtension: data.fileExtension,
        fileSize: data.fileSize,
        duration: Math.round(duration / 1000),
        reason
      }
    });
  }

  // Remove the activity from the context manager
  sentryContextManager.removeActivity('upload', itemId);
}
